import React, { useState, useEffect } from 'react';
import './uploader.css';
import { reportService } from '../services';
import Button from '../components/buttons/Button';
import BackButton from '../components/buttons/BackButton';
import Card from '../components/Card/Card';
import { FaUpload, FaSync, FaCheck, FaTimes, FaMicrophone, FaFolder, FaPlay, FaPause } from 'react-icons/fa';
import { createLogger } from '../services/Debug';
function Uploader() {
  // ---------------------------
  // STATE VARIABLES
  // ---------------------------
  const [autoEnabled, setAutoEnabled] = useState(false);
  const log = createLogger('Uploader');
  // fileStates => { "file.mp3": "En attente" | "uploading" | "done" }
  const [fileStates, setFileStates] = useState({});


  // Actual MP3 file list
  const [mp3Files, setMp3Files] = useState([]);

  // Status message => store color & text
  const [statusMsg, setStatusMsg] = useState({ color: '', text: '' });

  // Device name for "deviceInfo"
  const [deviceName, setDeviceName] = useState(null);

  // ---------------------------
  // EFFECT: On Mount
  //  - add electron listeners
  //  - request initial file list & report types
  // ---------------------------
  useEffect(() => {
    // Handlers corresponding to your old IPC event listeners

    // 1) Called when we receive the current MP3 file list from main
    function handleMp3FilesList(mp3Array) {
      // Merge old file states so we keep 'uploading'/'done' where possible
      setFileStates((prev) => {
        const updated = { ...prev };
        // set "En attente" if it’s new
        mp3Array.forEach((f) => {
          if (!updated[f]) {
            updated[f] = 'En attente';
          }
        });
        // remove files that no longer exist
        Object.keys(updated).forEach((key) => {
          if (!mp3Array.includes(key)) {
            delete updated[key];
          }
        });
        return updated;
      });

      // Keep track of the new file list
      setMp3Files(mp3Array);

      // no automatic retrieval here
    }

    // 2) Called when auto‑retrieve is toggled in main or elsewhere
    function handleAutoRetrieveUpdated(enabled) {
      setAutoEnabled(enabled);
      setStatusMsg({
        color: 'green',
        text: enabled ? 'Récupération auto ACTIVÉE' : 'Récupération auto désactivée',
      });
    }

    // 3) Called when a single file upload has finished
    function handleRetrievalFinished({ fileName, serverMessage }) {
      setFileStates((prevStates) => {
        const updated = { ...prevStates };
        updated[fileName] = 'done';
        return updated;
      });
      setStatusMsg({
        color: '#28a745',
        text: serverMessage
          ? `(${fileName}) ${serverMessage}`
          : `Fichier '${fileName}' récupéré avec succès.`,
      });
      // Refresh after a short delay
      setTimeout(() => {
        window.electronAPI.requestFileList();
      }, 1000);
    }

    // 4) Called when retrieval or upload has an error
    function handleRetrievalError(errorMsg) {
      setStatusMsg({ color: 'red', text: `Erreur: ${errorMsg}` });
    }

    // 5) Called when a dictaphone device is mounted/unmounted
    function handleDeviceMounted({ isMounted, deviceName }) {
      if (isMounted) {
        setDeviceName(deviceName || 'PHILIPS');
        window.electronAPI.requestFileList();
      } else {
        setDeviceName(null);
        setMp3Files([]); // clear
        setFileStates({});
      }
    }


    window.electronAPI.onMp3FilesList(handleMp3FilesList);
    window.electronAPI.onAutoRetrieveUpdated(handleAutoRetrieveUpdated);
    window.electronAPI.onRetrievalFinished(handleRetrievalFinished);
    window.electronAPI.onRetrievalError(handleRetrievalError);
    window.electronAPI.onDeviceMounted(handleDeviceMounted);

    // Ask for initial data
    window.electronAPI.requestFileList();
    if (window.electronAPI.getAutoRetrieve) {
      window.electronAPI.getAutoRetrieve().then((enabled) => {
        setAutoEnabled(!!enabled);
        setStatusMsg({
          color: !!enabled ? 'green' : 'orange',
          text: !!enabled ? 'Récupération auto ACTIVÉE' : 'Récupération auto désactivée',
        });
      }).catch(() => {});
    }
    

    // CLEANUP: remove listeners if you have "removeListener" in electronAPI
    return () => {
      // example: window.electronAPI.removeMp3FilesList(handleMp3FilesList);
      // etc...
    };
  }, []);

  // ---------------------------
  // HANDLERS FOR BUTTONS
  // ---------------------------

  // Toggle auto retrieve
  function handleToggleAuto() {
    const newVal = !autoEnabled;
    setAutoEnabled(newVal);
    window.electronAPI.setAutoRetrieve(newVal);
    // We also show immediate feedback
    setStatusMsg({
      color: newVal ? 'green' : 'orange',
      text: newVal ? 'Récupération auto ACTIVÉE' : 'Récupération auto désactivée',
    });
  }

  // Manually retrieve now
  async function handleRetrieveNow() {
    for (const fileName of mp3Files) {
      setFileStates((prev) => ({ ...prev, [fileName]: 'uploading' }));
      try {
        const base64 = await window.electronAPI.readMp3File(fileName);
        if (!base64) throw new Error('Lecture du fichier impossible');

        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'audio/mpeg' });
        const file = new File([blob], fileName, { type: 'audio/mpeg' });

        await reportService.uploadVoice(file);
        await window.electronAPI.deleteMp3File(fileName);

        setFileStates((prev) => ({ ...prev, [fileName]: 'done' }));
        setStatusMsg({ color: '#28a745', text: `Fichier '${fileName}' envoyé` });
      } catch (err) {
        log.error('Erreur envoi fichier', err);
        setFileStates((prev) => ({ ...prev, [fileName]: 'En attente' }));
        setStatusMsg({ color: 'red', text: `Erreur: ${err.message}` });
      }
    }

    // Refresh list after uploads
    window.electronAPI.requestFileList();
  }

  // Close the window
  function handleClose() {
    window.close();
  }

  // ---------------------------
  // RENDERING
  // ---------------------------


  return (
    <div className={`uploader-container ${window.electronAPI ? 'electron-mode' : ''}`}>
      <BackButton to="/home" />
      <Card className="card-large">
        {/* Main Content */}
        <div className="uploader-content">
          {/* Device Status Section */}
          <div className="uploader-device-section">
            <div className="uploader-device-icon-wrapper">
              <FaFolder className={`uploader-device-icon ${deviceName ? 'uploader-connected' : 'uploader-disconnected'}`} />
              <div className={`uploader-connection-indicator ${deviceName ? 'uploader-connected' : ''}`} />
            </div>
            <div className="uploader-device-details">
              <span className="uploader-device-label">État de connexion</span>
              <span className="uploader-device-name">
                {deviceName ? `${deviceName} connecté` : 'Aucun appareil détecté'}
              </span>
            </div>
          </div>

          {/* Controls Section */}
          <div className="uploader-controls-section">
            <div className="uploader-control-buttons">
              <Button
                variant={autoEnabled ? "success" : "secondary"}
                size="medium"
                icon={autoEnabled ? FaPause : FaPlay}
                onClick={handleToggleAuto}
              >
                {autoEnabled ? 'Auto activé' : 'Auto désactivé'}
              </Button>
              
              <Button
                variant="primary"
                size="medium"
                icon={FaUpload}
                onClick={handleRetrieveNow}
                disabled={!mp3Files || mp3Files.length === 0}
              >
                Récupérer maintenant
              </Button>
            </div>

            {/* Status Message */}
            {statusMsg.text && (
              <div className="uploader-status-message" style={{ color: statusMsg.color }}>
                {statusMsg.text}
              </div>
            )}
          </div>

          {/* Files Section */}
          <div className="uploader-files-section">
            <div className="uploader-files-header">
              <h3>Fichiers détectés</h3>
              <span className="uploader-file-count">{mp3Files.length}</span>
            </div>
            
            <div className="uploader-file-list">
              {mp3Files.length === 0 ? (
                <div className="uploader-empty-state">
                  <FaFolder className="uploader-empty-icon" />
                  <p>Aucun fichier MP3 détecté</p>
                  <span>Connectez votre dictaphone pour voir les fichiers</span>
                </div>
              ) : (
                mp3Files.map((fileName) => {
                  const currentState = fileStates[fileName] || 'En attente';
                  return (
                    <div className="uploader-file-item" key={fileName}>
                      <div className="uploader-file-info">
                        <span className="uploader-file-name">{fileName}</span>
                        <span className={`uploader-file-status uploader-${currentState.toLowerCase().replace(' ', '-')}`}>
                          {currentState === 'uploading' && <FaSync className="uploader-status-icon uploader-spinning" />}
                          {currentState === 'done' && <FaCheck className="uploader-status-icon" />}
                          {currentState}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default Uploader;
